/**
 * Homologación visual de estados.
 * Los IDs corresponden a NeoEstadoSolicitud / Constantes.ESTADOS_SOLICITUD.
 */

export type HomeSolicitudFiltro =
  | 'todos'
  | 'con_oferta'
  | 'en_revision'
  | 'finalizados';

export type HomeSolicitudEstadoGrupo = Exclude<HomeSolicitudFiltro, 'todos'>;

export type HomeEstadoBadgeTone =
  | 'rojo' // FFCCCC / 953838
  | 'azul' // A5E9FF / 5CAABE
  | 'verde' // BAFFA5 / 2D8B2F
  | 'morado' // E7DBFF / 9B54BC
  | 'ambar'; // FFDEA6 / AE883D

export interface HomeSolicitudEstadoVisual {
  /** Grupo de filtro (si aplica); null = solo visible en Todos. */
  grupo: HomeSolicitudEstadoGrupo | null;
  /** Label del badge (estado deseado HU). */
  label: string;
  /** Clase CSS: home-estado-badge--{tone} */
  badgeClass: string;
}

export const HOME_SOLICITUD_FILTROS: ReadonlyArray<{
  id: HomeSolicitudFiltro;
  label: string;
}> = [
  { id: 'todos', label: 'Todos' },
  { id: 'con_oferta', label: 'Con oferta' },
  { id: 'en_revision', label: 'En revisión' },
  { id: 'finalizados', label: 'Finalizados' },
];

interface EstadoHomologacion {
  label: string;
  tone: HomeEstadoBadgeTone;
  /** Filtro de tabla; omitido = solo en Todos. */
  filtro?: HomeSolicitudEstadoGrupo;
}

/**
 * Mapa idEstado → homologación HU.
 * FINALIZADA (18): con desembolso → DESEMBOLSADO; sin desembolso → SIN OFERTAS.
 */
const HOMOLOGACION_POR_ID: Readonly<Record<number, EstadoHomologacion>> = {
  // EN BORRADOR MICROEMPRESARIO → EN PROCESO
  1: { label: 'EN PROCESO', tone: 'ambar' },
  // ENVIADA → EN REVISIÓN
  2: { label: 'EN REVISIÓN', tone: 'azul', filtro: 'en_revision' },
  // VALIDACION SARLAFT (no en tabla HU; se trata como en análisis operativo)
  3: { label: 'EN ANÁLISIS', tone: 'morado' },
  // RECHAZADA SARLAFT
  4: { label: 'NEGADO', tone: 'rojo', filtro: 'finalizados' },
  // DEVUELTA A MICROEMPRESARIO → DEVUELTA
  5: { label: 'DEVUELTA', tone: 'ambar' },
  // PENDIENTE PROPUESTAS ALIADOS → EN ANÁLISIS
  6: { label: 'EN ANÁLISIS', tone: 'morado', filtro: 'en_revision' },
  // REVISIÓN PROPUESTAS POR MICROEMPRESARIO → REVISIÓN OFERTAS
  7: { label: 'REVISIÓN OFERTAS', tone: 'azul', filtro: 'con_oferta' },
  // SIN PROPUESTAS / FINALIZADA (sin propuestas) → SIN OFERTAS
  8: { label: 'SIN OFERTAS', tone: 'rojo', filtro: 'finalizados' },
  // PROPUESTA ACEPTADA → camino a formalización
  9: { label: 'APROBADO', tone: 'verde', filtro: 'en_revision' },
  // RECHAZO PROPUESTAS POR MICROEMPRESARIO → DESISTIDO
  10: { label: 'DESISTIDO', tone: 'rojo', filtro: 'finalizados' },
  // PENDIENTE FORMALIZACIÓN → APROBADO
  11: { label: 'APROBADO', tone: 'verde', filtro: 'en_revision' },
  // DESEMBOLSADO ALIADO → DESEMBOLSADO
  12: { label: 'DESEMBOLSADO', tone: 'verde', filtro: 'finalizados' },
  // FINALIZADA (sin propuestas) → SIN OFERTAS; con desembolso → ver resolveHomeEstadoVisual
  18: { label: 'SIN OFERTAS', tone: 'rojo', filtro: 'finalizados' },
  // NEGADO DESEMBOLSO → NEGADO
  19: { label: 'NEGADO', tone: 'rojo', filtro: 'finalizados' },
  // SIN COBERTURA GEOGRAFICA → SIN COBERTURA
  20: { label: 'SIN COBERTURA', tone: 'ambar', filtro: 'finalizados' },
  // CANCELADA POR NO GESTIÓN → CANCELADA
  21: { label: 'CANCELADA', tone: 'rojo', filtro: 'finalizados' },
  // SIN COBERTURA POR FILTROS → SIN COBERTURA
  22: { label: 'SIN COBERTURA', tone: 'ambar', filtro: 'finalizados' },
};

function badgeClassFor(tone: HomeEstadoBadgeTone): string {
  return `home-estado-badge--${tone}`;
}

function visualFromRow(row: EstadoHomologacion): HomeSolicitudEstadoVisual {
  return {
    grupo: row.filtro ?? null,
    label: row.label,
    badgeClass: badgeClassFor(row.tone),
  };
}

/** NeoSolicitudCreditoDTO.solicitudDesembolsada === 1 tras desembolso aliado. */
export function esSolicitudConDesembolso(
  solicitudDesembolsada?: number | null
): boolean {
  return Number(solicitudDesembolsada) === 1;
}

export function resolveHomeEstadoGrupo(
  idEstado: number
): HomeSolicitudEstadoGrupo | null {
  const row = HOMOLOGACION_POR_ID[Number(idEstado)];
  return row?.filtro ?? null;
}

/**
 * Homologación visual de badges (HU 8099 líneas 37–51).
 */
export function resolveHomeEstadoVisual(
  idEstado: number,
  _estadoSolicitudApi?: string,
  solicitudDesembolsada?: number | null
): HomeSolicitudEstadoVisual {
  const id = Number(idEstado);

  if (
    id === 18 &&
    esSolicitudConDesembolso(solicitudDesembolsada) &&
    HOMOLOGACION_POR_ID[12]
  ) {
    return visualFromRow(HOMOLOGACION_POR_ID[12]);
  }

  const row = HOMOLOGACION_POR_ID[id];

  if (row) {
    return visualFromRow(row);
  }

  // Fallback desconocido: ámbar genérico
  return {
    grupo: null,
    label: _estadoSolicitudApi?.trim() || 'EN PROCESO',
    badgeClass: badgeClassFor('ambar'),
  };
}

export function matchesHomeFiltro(
  idEstado: number,
  filtro: HomeSolicitudFiltro
): boolean {
  if (filtro === 'todos') {
    return true;
  }
  return resolveHomeEstadoGrupo(idEstado) === filtro;
}
